import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import ReactSelect from 'react-select';
import { Trash2, X, Eye, FileText, Download } from 'lucide-react';
import { getAdminHelpdeskTickets, updateTicketStatus, deleteTicket } from '../../lib/apiService';

const STATUS_STYLES = {
  open: { border: '#bfdbfe', text: '#1d4ed8', bg: '#eff6ff' },
  in_progress: { border: '#fed7aa', text: '#c2410c', bg: '#fff7ed' },
  resolved: { border: '#a7f3d0', text: '#047857', bg: '#ecfdf5' },
  closed: { border: '#e2e8f0', text: '#334155', bg: '#f8fafc' }
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed'
};

const statusOptions = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

const getSelectStyles = (status) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.closed;
  return {
    control: (base, state) => ({
      ...base,
      minHeight: '36px',
      height: '36px',
      borderRadius: '9999px',
      fontSize: '12px',
      fontWeight: '600',
      borderColor: state.isFocused ? s.border : s.border,
      backgroundColor: s.bg,
      boxShadow: 'none',
      '&:hover': { borderColor: s.border }
    }),
    valueContainer: (base) => ({
      ...base,
      height: '36px',
      padding: '0 12px',
      display: 'flex',
      alignItems: 'center'
    }),
    singleValue: (base) => ({
      ...base,
      color: s.text
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '4px 9px 4px 4px',
      color: s.text,
      '&:hover': { color: s.text }
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      fontSize: '12px',
      backgroundColor: state.isSelected ? s.bg : state.isFocused ? '#f1f5f9' : 'transparent',
      color: '#0f172a',
      cursor: 'pointer',
      '&:active': { backgroundColor: '#e2e8f0' }
    })
  };
};

const HelpdeskPage = () => {
  const { userProfile } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingTicket, setViewingTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    if (!userProfile) return;
    try {
      const res = await getAdminHelpdeskTickets();
      setTickets(res?.tickets || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const res = await updateTicketStatus(ticketId, newStatus);
      if (res.status) {
        toast.success(res.message || 'Status updated');
        // Update local state without refetching immediately for speed
        setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: newStatus } : t));
      } else {
        toast.error(res.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ticket permanently?')) return;
    try {
      const res = await deleteTicket(id);
      if (res.status) {
        toast.success(res.message || 'Ticket deleted');
        fetchTickets();
      } else {
        toast.error(res.message || 'Failed to delete ticket');
      }
    } catch (err) {
      toast.error('Failed to delete ticket');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Helpdesk Management | JoldiPabo Admin</title>
      </Helmet>

      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Helpdesk Tickets
        </h1>
        <p className="text-sm text-slate-600 mt-1">Manage user support requests and issues</p>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">User Info</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Category & Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-900">{t.name}</span>
                      <span className="text-xs text-slate-500">{t.phone}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-slate-900">{t.category}</span>
                      <span className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-36">
                      <ReactSelect
                        styles={getSelectStyles(t.status)}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        options={statusOptions}
                        value={statusOptions.find(o => o.value === t.status)}
                        onChange={(opt) => handleStatusChange(t.id, opt?.value)}
                        isSearchable={false}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setViewingTicket(t)} title="View Details">
                        <Eye className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} title="Delete Ticket">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {tickets.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-slate-500">No helpdesk tickets found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* View Ticket Modal */}
      {viewingTicket && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <FileText className="w-5 h-5 text-[#ee7923]" />
                Ticket Details
              </h2>
              <button onClick={() => setViewingTicket(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 max-h-[75vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">User Details</p>
                  <p className="text-sm font-medium text-slate-900">{viewingTicket.name}</p>
                  <p className="text-sm text-slate-600">{viewingTicket.phone}</p>
                  {viewingTicket.email && <p className="text-sm text-slate-600">{viewingTicket.email}</p>}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Ticket Info</p>
                  <p className="text-sm text-slate-900"><span className="font-medium text-slate-600">Category:</span> {viewingTicket.category}</p>
                  <p className="text-sm text-slate-900"><span className="font-medium text-slate-600">Status:</span> {STATUS_LABELS[viewingTicket.status]}</p>
                  <p className="text-sm text-slate-900"><span className="font-medium text-slate-600">Created:</span> {new Date(viewingTicket.created_at).toLocaleString()}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Message</p>
                <div className="bg-slate-50 p-4 rounded-xl text-sm text-slate-700 whitespace-pre-wrap border border-slate-100">
                  {viewingTicket.message}
                </div>
              </div>

              {viewingTicket.attachment_url && (
                <div className="mt-6">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Attachment</p>
                  <a
                    href={viewingTicket.attachment_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 transition-colors"
                  >
                    <Download className="w-4 h-4" /> View/Download Attachment
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpdeskPage;
